'use client';

import { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useLanguage } from '@/i18n/LanguageProvider';
import { appLanguageToContentLocale } from '@/i18n/contentLocale';
import 'github-markdown-css/github-markdown-dark.css';

interface LegalDocumentProps {
  documentPath: string;
}

const LegalDocument = ({ documentPath }: LegalDocumentProps) => {
  const [content, setContent] = useState<string>('');
  const { language } = useLanguage();

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const basePath = documentPath.replace(/^\//, '');
        const lang = appLanguageToContentLocale(language);
        const localizedPath = basePath.replace(/_en\.md$/, `_${lang}.md`);
        console.log('Fetching:', `/${localizedPath}`);
        const response = await fetch(`/${localizedPath}`);
        if (!response.ok) throw new Error('Failed to fetch document');
        const text = await response.text();
        setContent(text);
      } catch (error) {
        console.error('Error loading document:', error);
        setContent('Document not found');
      }
    };

    fetchContent();
  }, [documentPath, language]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1C1C1C] to-[#252525]">
      <div className="max-w-2xl mx-auto px-4 py-8">
        <article className="markdown-body" style={{ background: 'transparent', color: '#FAF9F6' }}>
          <style>{`
            .markdown-body {
              background-color: transparent !important;
              color: #FAF9F6 !important;
            }
            .markdown-body h1,
            .markdown-body h2,
            .markdown-body h3,
            .markdown-body h4,
            .markdown-body h5,
            .markdown-body h6 {
              color: #FAF9F6 !important;
              border-bottom: none !important;
              padding-bottom: 0 !important;
            }
            .markdown-body p {
              color: #FAF9F6 !important;
            }
            .markdown-body ul > li {
              list-style: none !important;
              position: relative;
              padding-left: 1.2em;
              color: #FAF9F6 !important;
            }
            .markdown-body ul > li::before {
              content: '-';
              position: absolute;
              left: 0;
              color: inherit;
            }
            .markdown-body a {
              color: #B9AE9A !important;
            }
            .markdown-body a:hover {
              color: #FAF9F6 !important;
            }
            .markdown-body code {
              background-color: rgba(185, 174, 154, 0.1) !important;
              color: #FAF9F6 !important;
            }
            .markdown-body pre {
              background-color: rgba(185, 174, 154, 0.1) !important;
              color: #FAF9F6 !important;
            }
            .markdown-body blockquote {
              border-left-color: #B9AE9A !important;
              color: #FAF9F6 !important;
            }
            .markdown-body table {
              color: #FAF9F6 !important;
            }
            .markdown-body table th,
            .markdown-body table td {
              border-color: rgba(185, 174, 154, 0.2) !important;
            }
            .markdown-body hr {
              border-color: rgba(185, 174, 154, 0.2) !important;
            }
          `}</style>
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
        </article>
      </div>
    </div>
  );
};

export default LegalDocument; 